/**
 * Widget Preview Page
 * Shows tenant's widget embed code and a preview of their widget after login.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, ExternalLink, Settings, Code, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tenantAPI } from '../utils/api';
import { logger } from '../utils/logger';

const WidgetPreview = () => {
    const { user, tenant } = useAuth();
    const navigate = useNavigate();
    const [widgetSettings, setWidgetSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('embed'); // 'embed' or 'preview'

    useEffect(() => {
        const fetchWidgetSettings = async () => {
            if (!tenant?.id) {
                setLoading(false);
                return;
            }

            try {
                const settings = await tenantAPI.getWidgetSettings(tenant.id);
                setWidgetSettings(settings);
            } catch (error) {
                logger.error('Error fetching widget settings:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchWidgetSettings();
    }, [tenant?.id]);

    const getEmbedCode = () => {
        if (!widgetSettings?.widget_public_key || !tenant?.id) {
            return '// Widget settings not configured yet';
        }

        const widgetUrl = process.env.REACT_APP_WIDGET_URL || window.location.origin;

        return `<!-- G6th Sense Chat Widget -->
<script>
  window.G6thSenseConfig = {
    tenantId: "${tenant.id}",
    widgetPublicKey: "${widgetSettings.widget_public_key}"
  };
</script>
<script src="${widgetUrl}/widget-loader.js" async></script>`;
    };

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(getEmbedCode());
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            logger.error('Failed to copy:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <img
                                src="/Logo.png"
                                alt="G6th Sense"
                                className="h-10 w-auto"
                            />
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900">
                                    Widget Setup
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {tenant?.company_name || 'Your Company'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate('/admin/settings')}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            >
                                <Settings size={18} className="mr-2" />
                                Widget Settings
                            </button>
                            <button
                                onClick={() => navigate('/admin')}
                                className="inline-flex items-center px-5 py-2.5 bg-green-600 text-white rounded-lg text-base font-semibold hover:bg-green-700 transition-colors shadow-md"
                            >
                                Admin Panel
                                <ExternalLink size={18} className="ml-2" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Welcome Message */}
                <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 mb-8 text-white">
                    <h2 className="text-2xl font-bold mb-2">
                        Welcome, {user?.name || 'User'}!
                    </h2>
                    <p className="text-green-100">
                        Your chat widget is ready. Copy the embed code below and add it to your website to start engaging with visitors.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex space-x-4 mb-6">
                    <button
                        onClick={() => setActiveTab('embed')}
                        className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeTab === 'embed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <Code size={18} className="mr-2" />
                        Embed Code
                    </button>
                    <button
                        onClick={() => setActiveTab('preview')}
                        className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeTab === 'preview'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <Eye size={18} className="mr-2" />
                        Preview
                    </button>
                </div>

                {activeTab === 'embed' ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Embed Code
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Add this code to your website's HTML, just before the closing &lt;/body&gt; tag.
                                    </p>
                                </div>
                                <button
                                    onClick={handleCopyCode}
                                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        copied
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {copied ? (
                                        <>
                                            <Check size={18} className="mr-2" />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={18} className="mr-2" />
                                            Copy Code
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="p-6 bg-gray-900">
                            <pre className="text-sm text-gray-100 overflow-x-auto">
                                <code>{getEmbedCode()}</code>
                            </pre>
                        </div>

                        {/* Allowed Domains */}
                        <div className="p-6 border-t border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                Allowed Domains
                            </h4>
                            <p className="text-sm text-gray-500 mb-3">
                                Your widget will only work on these domains for security:
                            </p>
                            {widgetSettings?.allowed_domains?.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {widgetSettings.allowed_domains.map((domain, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                                        >
                                            {domain}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-amber-600">
                                    No domains configured. Go to Widget Settings to add allowed domains.
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Widget Preview
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            This is how your chat widget will appear on your website.
                        </p>
                        <div className="relative bg-gray-100 rounded-xl h-[600px] overflow-hidden">
                            {/* Preview iframe - loads the widget with tenant config via query params */}
                            {widgetSettings?.widget_public_key && tenant?.id ? (
                                <iframe
                                    src={`/widget?tenantId=${tenant.id}&widgetPublicKey=${widgetSettings.widget_public_key}`}
                                    title="Widget Preview"
                                    className="absolute inset-0 w-full h-full border-0"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                        <p className="text-gray-500">Widget not configured yet.</p>
                                        <button
                                            onClick={() => navigate('/admin/settings')}
                                            className="mt-2 text-green-600 hover:text-green-700 font-medium"
                                        >
                                            Go to Settings to configure
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Quick Links */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => navigate('/admin')}
                        className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-green-300 hover:shadow-md transition-all text-left"
                    >
                        <h4 className="font-semibold text-gray-900 mb-1">Dashboard</h4>
                        <p className="text-sm text-gray-500">View analytics and visitor activity</p>
                    </button>
                    <button
                        onClick={() => navigate('/admin/chats')}
                        className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-green-300 hover:shadow-md transition-all text-left"
                    >
                        <h4 className="font-semibold text-gray-900 mb-1">Chat Sessions</h4>
                        <p className="text-sm text-gray-500">View and respond to visitor chats</p>
                    </button>
                    <button
                        onClick={() => navigate('/admin/knowledge')}
                        className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-green-300 hover:shadow-md transition-all text-left"
                    >
                        <h4 className="font-semibold text-gray-900 mb-1">Knowledge Base</h4>
                        <p className="text-sm text-gray-500">Train your AI with your content</p>
                    </button>
                </div>
            </main>
        </div>
    );
};

export default WidgetPreview;
